import { useEffect, useState } from 'react';
import { useContentSelector } from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';

import {
  ComponentFormBuilder,
  InputFieldWithSelectorOptions,
  InputFieldWithSelectorOptionsAndValues,
} from './ComponentFormBuilder';
import { useGetComponentByIdMultilingual } from '@wajeez-web/components/helpers';
import { Languages } from '@wajeez-web/core/utils';

export interface ComponentBody {
  referenceName: string | undefined;
  eventName: string | undefined;
  pageNumber: number | undefined;
  pageSize: number | undefined;
  profiles: {
    fields: InputFieldWithSelectorOptionsAndValues[];
    languageId: Languages;
  }[];
  selectors: {
    defaultCritiria?: string | undefined;
    factor?: Server.SelectFactor;
    defaultRelationLength?: number | undefined;
    defaultTypeId?: number | undefined;
    defaultEntityId?: string | undefined;
  }[];
  sorters: Server.CollectionComponentContentEntitySorter[];
  itemTemplate?: string | undefined;
}

interface ComponentEditFormProps {
  inputFieldsWithSelectorOptions: InputFieldWithSelectorOptions[] | undefined;
  onSubmit: () => void;
  onItemTemplateCloneSaveAndEdit: (itemTemplateId: string) => void;
  onItemTemplateEditButtonClick: (itemTemplateId: string) => void;
  onCancel?: () => void;
}

export const ComponentEditForm = ({
  inputFieldsWithSelectorOptions,
  onSubmit,
  onItemTemplateCloneSaveAndEdit,
  onItemTemplateEditButtonClick,
  onCancel,
}: ComponentEditFormProps) => {
  const [
    inputFieldsWithSelectorOptionsAndValues,
    setInputFieldsWithSelectorOptionsAndValues,
  ] = useState<ComponentBody | undefined>();

  const componentType = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypeById
  );
  const componentTypeData = componentType.data;

  const componentLookupsByComponentTypeIds = useContentSelector(
    ({ components }) => components.componentLookupsByComponentTypeId
  );

  const { data: componentDetailsMultilingualData } =
    useGetComponentByIdMultilingual();

  useEffect(() => {
    if (componentDetailsMultilingualData && inputFieldsWithSelectorOptions) {
      const fieldsWithValues: ComponentBody = {
        referenceName: componentDetailsMultilingualData.referenceName,
        eventName: componentDetailsMultilingualData.eventName,
        pageNumber: componentDetailsMultilingualData.pageNumber,
        pageSize: componentDetailsMultilingualData.pageSize,
        itemTemplate: componentDetailsMultilingualData.itemTemplateId,
        profiles:
          componentDetailsMultilingualData.profiles?.map((p) => {
            return {
              languageId: p.languageId!,
              fields:
                inputFieldsWithSelectorOptions.map((i) => {
                  let value: string | undefined;

                  const parameter = p.parameters?.find(
                    (p) => p.name === i.name
                  );

                  if (i.fieldType === Server.FieldType.PredefinedComponent) {
                    value = parameter?.type;
                  } else if (i.fieldType === Server.FieldType.MappedField) {
                    value = JSON.stringify(
                      parameter?.mappingOptions?.map((o) => {
                        return o.entityTypeFieldId!;
                      })
                    );
                  } else if (
                    i.fieldType === Server.FieldType.RequiredComponent
                  ) {
                    value = parameter?.id;
                  } else {
                    value = parameter?.value;
                  }

                  return {
                    ...i,
                    value,
                  };
                }) || [],
            };
          }) || [],
        selectors:
          componentDetailsMultilingualData.selectors?.map((s) => {
            return {
              ...s,
              defaultCritiria: s.defaultCriteria,
            };
          }) || [],
        sorters: componentDetailsMultilingualData.sorters || [],
      };

      setInputFieldsWithSelectorOptionsAndValues(fieldsWithValues);
    }
  }, [componentDetailsMultilingualData, inputFieldsWithSelectorOptions]);

  return (
    <div className="w-full mx-auto">
      {componentDetailsMultilingualData &&
        inputFieldsWithSelectorOptions &&
        componentTypeData &&
        inputFieldsWithSelectorOptionsAndValues && (
          <ComponentFormBuilder
            type="Edit"
            dynamicFields={
              inputFieldsWithSelectorOptionsAndValues?.profiles[0].fields
            }
            profiles={inputFieldsWithSelectorOptionsAndValues.profiles}
            componentTypeId={Number(componentDetailsMultilingualData?.typeId)}
            onSubmit={onSubmit}
            onItemTemplateCloneSaveAndEdit={onItemTemplateCloneSaveAndEdit}
            onItemTemplateEditButtonClick={onItemTemplateEditButtonClick}
            itemTemplateField={
              componentTypeData?.contentItemType === 1
                ? {
                    name: 'itemTemplate',
                    label: 'Item Template',
                    selectOptions: componentLookupsByComponentTypeIds.typeIds[
                      JSON.stringify(componentTypeData.allowedItemTemplateTypes)
                    ]?.data.map((d) => {
                      return {
                        value: d.id,
                        label: d.referenceName,
                      };
                    }),
                    value: componentDetailsMultilingualData?.itemTemplateId,
                  }
                : undefined
            }
            referenceName={
              inputFieldsWithSelectorOptionsAndValues?.referenceName
            }
            eventName={inputFieldsWithSelectorOptionsAndValues?.eventName}
            pageNumber={inputFieldsWithSelectorOptionsAndValues?.pageNumber}
            pageSize={inputFieldsWithSelectorOptionsAndValues?.pageSize}
            selectors={inputFieldsWithSelectorOptionsAndValues?.selectors}
            sorters={inputFieldsWithSelectorOptionsAndValues?.sorters}
            onCancel={onCancel}
          />
        )}
    </div>
  );
};
